import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { doc, setDoc, collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { auth, db } from '../../firebaseconfig';
import './Savings.css';

const AddStaffModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    phoneNumber: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [nextStaffId, setNextStaffId] = useState('');

  useEffect(() => {
    generateNextStaffId();
  }, []);

  const generateNextStaffId = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'staff'), orderBy('staffId', 'desc'));
      const querySnapshot = await getDocs(q);
      
      let lastNumber = 0;
      if (!querySnapshot.empty) {
        const lastStaffId = querySnapshot.docs[0].data().staffId;
        lastNumber = parseInt(lastStaffId.slice(-3));
      }
      
      const nextNumber = lastNumber + 1;
      const newStaffId = `STF${nextNumber.toString().padStart(3, '0')}`;
      setNextStaffId(newStaffId);
    } catch (err) {
      console.error('Error generating staff ID:', err);
      setError('Failed to generate staff ID');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const createStaffAccount = async () => {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Create user document with staff role
      const userDoc = doc(collection(db, 'users'), userCredential.user.uid);
      await setDoc(userDoc, {
        email: formData.email,
        displayName: formData.displayName,
        phoneNumber: formData.phoneNumber,
        staffId: nextStaffId,
        createdAt: new Date(),
        active: true,
        role: 'staff',
        accessLevel: 'readonly'
      });

      // Initialize savings balance document
      const savingsDoc = doc(collection(db, 'staff_savings'), userCredential.user.uid);
      await setDoc(savingsDoc, {
        currentBalance: 0,
        lastUpdated: new Date(),
        staffId: nextStaffId,
        staffName: formData.displayName
      });

      return true;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await createStaffAccount();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating staff:', err);
      
      const errorMessages = {
        'auth/email-already-in-use': 'This email is already registered. Please use a different email address.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password should be at least 6 characters long.',
        'auth/network-request-failed': 'Network error. Please check your internet connection.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
      };

      setError(errorMessages[err.code] || 'Failed to create staff account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Add New Staff</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            {nextStaffId && (
              <div className="form-group">
                <label>Staff ID</label>
                <div className="staff-id-display">{nextStaffId}</div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address*</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter staff email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password*</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
                placeholder="Enter password"
              />
              <small className="form-help">Minimum 6 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="displayName">Full Name*</label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                required
                placeholder="Enter staff full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>

            <div className="modal-footer">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-button"
                disabled={loading || !nextStaffId}
              >
                {loading ? 'Creating Staff...' : 'Create Staff'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddStaffModal; 